import { injectable } from 'inversify';
import { Arr } from '../../../CodesForFoundation';
import { Component } from '../../../concepts/objects';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { resolve } from '../../Container';
import { DefineDimension } from '../dataExport/addons/SimpleDimension';
import { FromFirstMatchExportAdapter } from '../dataExport/ExportAdapter';
import { ExportBlock, ExportDimension, ExportValue } from '../dataExport/ExportDimension';
import { Comment } from './Comment';
import { Commentable } from './Commentable';

@injectable()
export class CommentExportAdapter extends FromFirstMatchExportAdapter {

    @resolve(ParticipantManager) private participants!: ParticipantManager;

    protected selector = (c: Component) => c.is(Commentable);

    public readonly id = 'CommentAssocations';
    public readonly label = 'Associations';
    public readonly defaults = false;
    public readonly columns = [
        DefineDimension(
            'CommentsGenerated',
            'Comments',
            'Exports the comments associated with each object, generating one column per comment.',
            (type, params) => ExportDimension({
                id: 'Comments',
                label: 'Comments',
                params,
                type,
                read: createReadWithScope(this.participants)
            }),
        )
    ];
}

const createReadWithScope = (participants: ParticipantManager) =>
    function (this: ExportDimension, rows: Component[]) {

        const lookup = new Map<Component, Comment[]>();
        const block = [] as ExportBlock[];

        for (const c of rows) {
            const ccs = Arr.sortBy(c.polarize(Comment), 'created');
            lookup.set(c, ccs);
            while (block.length < ccs.length) {
                block.push([]);
            }
        }

        return block.map((col, i) => {
            const head = [ExportValue([this.id, i.toString()], 'Comment #' + (i + 1))];
            const body = rows.map(c => {
                const tm = lookup.get(c)[i];
                if (!tm) return ExportValue([this.id, i.toString()], '');
                const u = participants.get(tm.userId || '') ?? null;
                return ExportValue([this.id, i.toString()], `${u?.name ?? tm.userId}:\r\n${tm.content}`);
            });
            return head.concat(body);
        });
    };
